import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { calendarsAPI, serviceDeliveryAPI } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Calendar, Plus, Users, CheckCircle2, Palmtree, Lock } from 'lucide-react';
import { CalendarGrid, dayLabel, localISO } from './CalendarGrid';
import { CalendarItemRow } from './CalendarItemRow';
import { CeoEventDialog, CEO_TYPE_CFG, emptyCeoForm } from './CeoEventDialog';
import { GoogleSyncPanel } from './GoogleSyncPanel';
import { EventReminderSelect } from './EventReminderSelect';

const TEAM_CFG = {
  reunion: { icon: Users, dot: 'bg-blue-500', text: 'text-blue-600', block: 'bg-blue-100 text-blue-950 border-blue-500', label: 'Reunión' },
  reunion_cliente: { icon: Users, dot: 'bg-blue-500', text: 'text-blue-600', block: 'bg-blue-100 text-blue-950 border-blue-500', label: 'Reunión con cliente' },
  shooting: { icon: Users, dot: 'bg-purple-500', text: 'text-purple-600', block: 'bg-purple-100 text-purple-950 border-purple-500', label: 'Grabación' },
  tarea: { icon: CheckCircle2, dot: 'bg-emerald-500', text: 'text-emerald-600', block: 'bg-emerald-100 text-emerald-950 border-emerald-500', label: 'Tarea', isTask: true },
  ausencia: { icon: Palmtree, dot: 'bg-rose-500', text: 'text-rose-600', block: 'bg-rose-100 text-rose-950 border-rose-500', label: 'Ausencia' },
};

export default function CeoCalendarPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = localISO(new Date());
  const [tab, setTab] = useState('ours');
  const [range, setRange] = useState(null);
  const [items, setItems] = useState([]);
  const [teamData, setTeamData] = useState({ members: [], items: [] });
  const [memberFilter, setMemberFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [google, setGoogle] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyCeoForm(null, today));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const g = params.get('gcal');
    if (g) {
      if (g === 'connected') toast.success('Google Calendar conectado. Elige en "Configurar" qué calendarios sincronizar.');
      else if (g === 'noscope') toast.error('Google no concedió el permiso de Calendar. Vuelve a conectar y marca la casilla de Google Calendar en la pantalla de permisos.', { duration: 10000 });
      else toast.error('No se pudo conectar Google Calendar');
      params.delete('gcal'); setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    calendarsAPI.teamMembers().then((m) => setMembers(m.filter((u) => u.role === 'member'))).catch(() => {});
    serviceDeliveryAPI.getClients().then(setClients).catch(() => {});
    calendarsAPI.googleStatus().then(setGoogle).catch(() => {});
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!range) return;
    if (!silent) setLoading(true);
    try {
      const [ours, team] = await Promise.all([calendarsAPI.ceoRange(range.from, range.to), calendarsAPI.teamAvailability(range.from, range.to)]);
      setItems(ours?.items || []);
      setTeamData(team || { members: [], items: [] });
    } catch (e) { console.error('Error loading CEO calendar:', e); }
    finally { if (!silent) setLoading(false); }
  }, [range]);
  useEffect(() => { load(); const id = setInterval(() => load(true), 30000); return () => clearInterval(id); }, [load]);

  const visibleItems = useMemo(() => typeFilter ? items.filter((i) => i.type === typeFilter) : items, [items, typeFilter]);
  const teamItems = useMemo(() => {
    const list = memberFilter ? teamData.items.filter((i) => i.user_id === memberFilter) : teamData.items;
    return memberFilter ? list : list.map((i) => (i.type === 'ausencia' ? i : { ...i, title: `${i.user_name}: ${i.title}` }));
  }, [teamData, memberFilter]);
  const byDate = (list) => list.reduce((m, it) => { if (it.date) (m[it.date] = m[it.date] || []).push(it); return m; }, {});

  const openAdd = (iso, hora) => {
    const f = emptyCeoForm(iso, today);
    if (hora) {
      const [h, m] = hora.split(':').map(Number);
      f.hora_inicio = hora;
      f.hora_fin = `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    } else if (hora === null) {
      f.all_day = true;
    }
    setForm(f); setDialog(true);
  };
  const openEdit = (it) => {
    setForm({
      id: it.id, kind: it.kind || 'event', type: it.type, titulo: it.title || '', fecha: it.fecha || it.date, fecha_fin: it.fecha_fin || '',
      all_day: !!it.all_day, hora_inicio: it.time || '', hora_fin: it.end_time || '', meet_link: it.meet_link || '', generar_meet: false,
      descripcion: it.descripcion || '', client_id: it.client_id || '', privado: !!it.privado, invitados: it.invitados || [],
      invitar_todos: !!it.invitar_todos, completada: !!it.completada, gcal_event_id: it.gcal_event_id || null,
      gcal_series_id: it.gcal_series_id || null,
    });
    setDialog(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form, titulo: form.titulo.trim(), fecha_fin: form.fecha_fin || null, hora_inicio: form.hora_inicio || null, hora_fin: form.hora_fin || null,
        meet_link: form.meet_link || null, descripcion: form.descripcion || null, client_id: form.client_id || null,
      };
      delete payload.id;
      if (form.id) await calendarsAPI.updateCeoEvent(form.id, payload); else await calendarsAPI.createCeoEvent(payload);
      toast.success(form.id ? 'Guardado' : 'Añadido a tu calendario');
      setDialog(false); await load(true);
    } catch (e) { toast.error(e?.message || 'No se pudo guardar'); }
    setSaving(false);
  };
  const [sharing, setSharing] = useState(false);
  const handleTeamShare = async (v) => {
    setSharing(true);
    try {
      const r = await calendarsAPI.setTeamShare(form.id, v);
      setForm((f) => ({ ...f, invitar_todos: v, invitados: v ? [] : f.invitados }));
      toast.success(v ? `Compartido con el equipo (${r.eventos} ${r.eventos === 1 ? 'fecha' : 'fechas'})` : 'Ya no se muestra al equipo');
      await load(true);
    } catch (e) { toast.error(e?.message || 'No se pudo cambiar'); }
    setSharing(false);
  };
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async (it) => {
    const title = it.title || it.titulo || 'Sin título';
    setDeleting(true);
    try {
      await calendarsAPI.deleteCeoEvent(it.id);
      setDialog(false); setSelectedDay(null);
      await load(true);
      toast.success(`«${title}» eliminado`, {
        duration: 10000,
        action: { label: 'Deshacer', onClick: async () => { try { await calendarsAPI.restoreCeoEvent(it.id); toast.success('Restaurado'); await load(true); } catch (e) { toast.error(e?.message || 'No se pudo restaurar'); } } },
      });
    } catch (e) { toast.error(e?.message || 'No se pudo eliminar'); }
    setDeleting(false);
  };
  const toggleDone = async (it) => {
    try { await calendarsAPI.updateCeoEvent(it.id, { completada: !it.completada }); await load(true); } catch (e) { toast.error('No se pudo actualizar'); }
  };
  const handleOpen = (it) => { if (it.link) navigate(it.link); else if (it.can_edit) openEdit(it); };
  const handleMove = async (it, { fecha, hora_inicio, hora_fin }) => {
    const prev = items;
    setItems((list) => list.map((x) => (x.id === it.id && x.date === it.date ? { ...x, date: fecha, fecha, time: hora_inicio, end_time: hora_fin } : x)));
    try {
      await calendarsAPI.updateCeoEvent(it.id, { fecha, hora_inicio, hora_fin, all_day: false });
      toast.success(`«${it.title}» → ${fecha.split('-').reverse().join('/')} ${hora_inicio}–${hora_fin}`);
      await load(true);
    } catch (e) { setItems(prev); toast.error(e?.message || 'No se pudo mover el evento'); }
  };

  const renderOurs = (list, iso, showAdd = true) => (
    list.length === 0 ? (
      <div className="text-center py-10">
        <p className="text-sm text-muted-foreground">Nada este día</p>
        {showAdd && <Button variant="outline" size="sm" className="mt-3" onClick={() => openAdd(iso)} data-testid="ceo-add-empty-btn"><Plus className="w-4 h-4 mr-1" /> Añadir</Button>}
      </div>
    ) : list.map((it, idx) => (
      <CalendarItemRow key={`${it.source_kind}-${it.id}-${idx}`} it={it} cfg={CEO_TYPE_CFG[it.type] || CEO_TYPE_CFG.evento}
        onOpen={(it.link || it.can_edit) ? handleOpen : undefined} onEdit={it.can_edit ? openEdit : undefined}
        onDelete={it.can_edit ? ((x) => window.confirm(`¿Eliminar "${x.title}"?`) && handleDelete(x)) : undefined} onToggleDone={it.can_edit && it.kind === 'task' ? toggleDone : undefined}
        testId={`ceo-item-${it.type}-${it.id}`} />
    ))
  );
  const renderTeam = (list) => (
    list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-10">El equipo no tiene nada este día</p>
      : list.map((it, idx) => <CalendarItemRow key={`${it.source_kind}-${it.id}-${it.user_id}-${idx}`} it={it} cfg={TEAM_CFG[it.type] || TEAM_CFG.reunion} onOpen={it.link ? handleOpen : undefined} testId={`team-item-${it.type}-${it.id}`} />)
  );

  const typeFilterUI = (
    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded-lg px-2 py-1 text-sm bg-background h-8" data-testid="ceo-type-filter">
      <option value="">Todos los tipos</option>
      {Object.entries(CEO_TYPE_CFG).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
    </select>
  );
  const memberFilterUI = (
    <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="border rounded-lg px-2 py-1 text-sm bg-background h-8" data-testid="team-member-filter">
      <option value="">Todo el equipo</option>
      {teamData.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
    </select>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="ceo-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Calendar className="w-7 h-7 text-primary" /> Mi Calendario</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Calendario de las CEOs: compartido entre Karmele y Marta, invisible para el equipo salvo los eventos a los que invitéis.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <EventReminderSelect />
          <GoogleSyncPanel onSynced={() => { load(true); calendarsAPI.googleStatus().then(setGoogle).catch(() => {}); }} />
          <Button onClick={() => openAdd()} data-testid="ceo-add-btn"><Plus className="w-4 h-4 mr-2" /> Añadir</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ours" data-testid="tab-ours">Nuestro calendario</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Equipo (disponibilidad)</TabsTrigger>
        </TabsList>
        <TabsContent value="ours">
          <Card><CardContent className="pt-5">
            <CalendarGrid items={visibleItems} typeCfg={CEO_TYPE_CFG} fallbackType="evento" loading={loading} headerExtra={typeFilterUI}
              onRangeChange={setRange} onDayClick={(iso) => setSelectedDay(iso)} onItemClick={handleOpen} onAddAt={openAdd}
              weekTimeGrid onSlotClick={(iso, hora) => openAdd(iso, hora)} onItemMove={handleMove}
              renderDayList={(list, iso) => renderOurs(list, iso)} />
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="team">
          <Card><CardContent className="pt-5">
            <p className="text-xs text-muted-foreground mb-3">Reuniones, tareas con fecha y ausencias de Ana, Juan y Leo. Solo lectura: sirve para conocer su disponibilidad.</p>
            <CalendarGrid items={teamItems} typeCfg={TEAM_CFG} fallbackType="reunion" loading={loading} headerExtra={memberFilterUI}
              onRangeChange={setRange} onDayClick={(iso) => setSelectedDay(iso)} onItemClick={handleOpen}
              weekTimeGrid onSlotClick={() => {}}
              renderDayList={(list) => renderTeam(list)} />
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-md" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">{selectedDay ? dayLabel(selectedDay) : ''}</DialogTitle>
            <DialogDescription>{tab === 'ours' ? 'Nuestro calendario' : 'Disponibilidad del equipo'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {selectedDay && (tab === 'ours' ? renderOurs(byDate(visibleItems)[selectedDay] || [], selectedDay, false) : renderTeam(byDate(teamItems)[selectedDay] || []))}
          </div>
          {tab === 'ours' && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { openAdd(selectedDay); setSelectedDay(null); }} data-testid="ceo-add-this-day-btn"><Plus className="w-4 h-4 mr-2" /> Añadir este día</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <CeoEventDialog open={dialog} onOpenChange={setDialog} form={form} setForm={setForm} members={members} clients={clients} googleConnected={google.connected} onSave={handleSave} saving={saving} onDelete={handleDelete} deleting={deleting} onTeamShare={handleTeamShare} sharing={sharing} />
    </motion.div>
  );
}
